import { useContext, useState, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Logo from "../assets/Logo.svg";
import { useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { createnewUser, loginUser } from "../services/userService";
import SocialIcon from "../assets/Social icon.svg";
import { AuthContext } from "../context/authContext";
import { Button, Input, message } from "antd";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import ReCAPTCHA from "react-google-recaptcha";

function Signup() {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const captchaRef = useRef(null);

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  const resetCaptcha = () => {
    if (captchaRef.current) {
      captchaRef.current.reset();
      setCaptchaValue(null);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      password: "",
      authProviderFlag: "local",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      password: Yup.string()
        .min(8, "Password must be at least 8 characters long")
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
        .matches(/[a-z]/, "Password must contain at least one lowercase letter")
        .matches(/[0-9]/, "Password must contain at least one number")
        .matches(
          /[\W_]/,
          "Password must contain at least one special character"
        )
        .required("Password is required"),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      if (!captchaValue) {
        message.error("Please complete the captcha");
        setSubmitting(false);
        return;
      }

      setIsLoading(true);
      try {
        const userData = {
          ...values,
        };
        const res = await createnewUser(userData);
        message.success(res.message);
        resetForm();
        resetCaptcha();
      } catch (error) {
        message.error(error.response?.data?.message || "Signup failed");
        resetCaptcha();
      } finally {
        setSubmitting(false);
        setIsLoading(false);
      }
    },
  });

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      const { access_token } = tokenResponse;

      if (access_token) {
        try {
          const response = await fetch(
            "https://www.googleapis.com/oauth2/v3/userinfo",
            {
              headers: {
                Authorization: `Bearer ${access_token}`,
              },
            }
          );
          const userInfo = await response.json();

          const newUser = {
            name: userInfo.name,
            email: userInfo.email,
            password: "",
            authProviderFlag: "google",
          };

          await createnewUser(newUser);
          const userData = {
            email: userInfo.email,
            password: "",
            authProviderFlag: "google",
          };

          const loginResponse = await loginUser(userData);
          login(loginResponse);
          message.success("Signup Successful");
          if (loginResponse.onboarding) {
            navigate("/chat");
          } else {
            navigate("/onboarding");
          }
        } catch (error) {
          resetCaptcha();
          message.error(error.response?.data?.message || "Signup failed");
        }
      } else {
        console.log("Access token not found");
      }
    },
    onError: () => {
      message.error("Login Failed try again later");
    },
  });

  return (
    <div className="w-full min-h-full flex justify-center items-center py-[40px]">
      <div className="flex flex-col w-[90%] xsm:w-[400px] md:w-[500px] gap-[20px] md:gap-[24px] py-[50px] md:py-[50px] px-[40px] md:px-[40px] shadow-2xl bg-white rounded-[32px]">
        <div className="flex justify-center items-center">
          <img
            src={Logo}
            alt="App Logo"
            className="w-[69.6px] h-[60px] md:w-[89.6px] md:h-[80px] cursor-pointer"
            onClick={() => window.location.href = "https://protvguide.com"}
          />
        </div>

        <div className="flex flex-col gap-[12px]">
          <h2 className="flex justify-center font-Poppins text-[24px] md:text-[30px] leading-[32px] md:leading-[38px] font-[700] text-center text-[#010101]">
            Sign up
          </h2>
          <p className="flex justify-center font-Poppins font-[400] text-[14px] md:text-[16px] leading-[22px] md:leading-[24px] text-center text-[#667085]">
            Create your account
          </p>
        </div>

        <form
          onSubmit={formik.handleSubmit}
          className="flex w-full flex-col gap-[20px]"
        >
          <div className="flex flex-col gap-[6px]">
            <label
              className="flex text-[#344054] font-Poppins text-[14px] not-italic font-[500] leading-[20px]"
              htmlFor="name"
            >
              Name
            </label>
            <Input
              className={`flex w-full py-[10px] px-[14px] gap-[8px] rounded-[8px] border-[1px] ${
                formik.touched.name && formik.errors.name
                  ? "border-red-500"
                  : "border-[#D0D5DD]"
              }`}
              id="name"
              name="name"
              type="text"
              placeholder="Full Name"
              {...formik.getFieldProps("name")}
            />
            {formik.touched.name && formik.errors.name ? (
              <div className="text-red-500 text-[12px] flex">
                {formik.errors.name}
              </div>
            ) : null}
          </div>

          <div className="flex flex-col gap-[6px]">
            <label
              className="flex text-[#344054] font-Poppins text-[14px] not-italic font-[500] leading-[20px]"
              htmlFor="email"
            >
              Email
            </label>
            <Input
              className={`flex w-full py-[10px] px-[14px] gap-[8px] rounded-[8px] border-[1px] ${
                formik.touched.email && formik.errors.email
                  ? "border-red-500"
                  : "border-[#D0D5DD]"
              }`}
              id="email"
              name="email"
              type="email"
              placeholder="Enter your email"
              {...formik.getFieldProps("email")}
            />
            {formik.touched.email && formik.errors.email ? (
              <div className="text-red-500 text-[12px] flex">
                {formik.errors.email}
              </div>
            ) : null}
          </div>

          <div className="relative flex flex-col gap-[6px]">
            <label
              className="flex text-[#344054] font-Poppins text-[14px] not-italic font-[500] leading-[20px]"
              htmlFor="password"
            >
              Password
            </label>
            <Input
              className={`flex w-full py-[10px] px-[14px] gap-[8px] rounded-[8px] border-[1px] ${
                formik.touched.password && formik.errors.password
                  ? "border-red-500"
                  : "border-[#D0D5DD]"
              }`}
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              {...formik.getFieldProps("password")}
            />
            {formik.touched.password && formik.errors.password ? (
              <div className="text-red-500 text-[12px] flex">
                {formik.errors.password}
              </div>
            ) : null}
            <button
              onClick={() => setShowPassword(!showPassword)}
              type="button"
              className="absolute top-9 right-2"
            >
              {showPassword ? (
                <EyeTwoTone twoToneColor="rgb(230, 31, 38)" />
              ) : (
                <EyeInvisibleOutlined style={{ color: "rgb(230, 31, 38)" }} />
              )}
            </button>
          </div>

          <ReCAPTCHA
            ref={captchaRef}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
            onChange={handleCaptchaChange}
          />

          <div className="flex w-full">
            <Button
              type="primary"
              htmlType="submit"
              disabled={formik.isSubmitting}
              className="bg-[#E61F26] h-[45px] w-full"
            >
              {isLoading ? "Signing up..." : "Sign up"}
            </Button>
          </div>
        </form>

        <div className="flex flex-col items-center w-full gap-[16px]">
          <div className="flex w-full rounded-[8px] border-[1px] border-[#D0D5DD]">
            <button
              onClick={() => handleGoogleLogin()}
              className="w-full flex items-center justify-center gap-[12px] py-[10px] px-[16px] rounded-[8px] hover:bg-[#D0D5DD]"
            >
              <img src={SocialIcon} alt="Google Social Icon" />
              <span className="text-[#344054] font-Poppins text-[16px] font-[600] leading-[24px]">
                Sign up with Google
              </span>
            </button>
          </div>
        </div>

        <div className="flex flex-col w-full items-center gap-[12px]">
          <div className="flex w-full justify-center items-center gap-[4px]">
            <span className="text-[#667085] font-Poppins text-[14px] font-[400] leading-[20px]">
              Already have an account?
            </span>
            <button
              onClick={() => navigate("/")}
              className="flex text-[#E61F26] bg-transparent font-Poppins text-[14px] font-[600] leading-[20px]"
            >
              Sign in
            </button>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-[4px] text-center px-4">
            <span className="text-[#667085] font-Poppins text-[12px] font-[400]">
              By continuing, you agree to our
            </span>
            <button
              type="button"
              onClick={() => navigate("/terms-of-use")}
              className="text-[#E61F26] hover:underline font-Poppins text-[12px] font-[600]"
            >
              Terms of Use
            </button>
            <span className="text-[#667085] font-Poppins text-[12px] font-[400]">
              and
            </span>
            <button
              type="button"
              onClick={() => navigate("/privacy-policy")}
              className="text-[#E61F26] hover:underline font-Poppins text-[12px] font-[600]"
            >
              Privacy Policy
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup;
